import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, map, tap } from 'rxjs/operators';
import { REGISTRY_STORAGE } from '../registry-storage.token';
import { FormGroup } from '@angular/forms';
import { cloneDeep } from '../../shared-third-party/functions/cloneDeep';
import { StorageInterface } from '../interfaces/storage.interface';
import { urldecode } from '../../shared-third-party/functions/urldecode';
import { ISortParams } from '../interfaces/i-sort-params';
import moment from 'moment';
import { isArray } from 'rxjs/internal-compatibility';

@Injectable()
export class RegistryService {
  // Количество записей
  countItems$: BehaviorSubject<number> = new BehaviorSubject(0);

  // Поток с извещением о необходимости добавить в url параметры значения по-умолчанию
  defaultParams$: Subject<void> = new Subject();

  // Тип сортировки
  sort$: Observable<string>;

  // Направление сортировки
  sortDirection$: Observable<string>;
  private _sortDirection = 'ASC';

  // Текущие параметры сортировки
  get sortParams() {
    const sortValue = cloneDeep(this._sortParams);
    // Если требуется развернуть сортировку
    const sortDirection = this._sortDirection;
    if (sortDirection === 'DESC') {
      for (const key in sortValue) {
        if (sortValue.hasOwnProperty(key)) {
          if (sortValue[key] === 'DESC') {
            sortValue[key] = 'ASC';
          } else if (sortValue[key] === 'ASC') {
            sortValue[key] = 'DESC';
          }
        }
      }
    }

    return sortValue;
  }

  private _sortParams: ISortParams = {};

  constructor(@Inject(REGISTRY_STORAGE) public storage: StorageInterface) {
    this.defaultParams$.pipe(debounceTime(1)).subscribe((_) => {
      this.storage.defaultState();
    });
    this.sort$ = this.storage.queryParams.pipe(map((params) => params?.sort || '1'));

    this.sortDirection$ = this.storage.queryParams.pipe(
      map((params) => params?.sortDirection || 'ASC'),
      tap((sortDirection) => (this._sortDirection = sortDirection)),
    );
  }

  public static createDefaultQueryParams(): { [key: string]: string } {
    return {
      page: '1',
      limit: '10',
      sort: '1',
      sortDirection: 'ASC',
    };
  }

  /**
   * Преобразователь параметров для запроса
   *
   * @param form Форма компонента
   * @param dateFormat
   */
  converterQueryParams(form: FormGroup, dateFormat: string = 'DD.MM.YYYYTHH:mm:ss'): any {
    let params = form.value;
    const queryParams = this.storage.currentState();
    for (const name in params) {
      if (params.hasOwnProperty(name)) {
        if (params[name] instanceof Date) {
          queryParams[name] = moment(params[name]).format(dateFormat);
        } else if (isArray(params[name])) {
          // Для обработки массивов доработать - на текущий момент в queryParams[name] запишется массив строк, а не строка.
          // queryParams[name] = params[name].map((item) => {
          //   return urldecode(item);
          // });
        } else if (params[name] === null) {
          queryParams[name] = 'null';
        } else {
          queryParams[name] = urldecode(params[name]);
        }
      }
    }

    for (const query in queryParams) {
      if (queryParams.hasOwnProperty(query)) {
        if (queryParams[query] === null || queryParams[query] === 'null') {
          delete queryParams[query];
          continue;
        }

        const contains = !!form.get(query);
        const disabled = contains && form.get(query)?.disabled;
        if (contains && disabled) {
          delete queryParams[query];
        }
      }
    }

    queryParams['page'] = '1';
    return queryParams;
  }

  set sortParams(sortParams: ISortParams) {
    this._sortParams = sortParams;
  }

  /**
   * Инициализация формы по параметрам
   *
   * @param form Форма компонента
   * @param queryParams Текущие параметры
   * @param datetimeControls Контролы с типом datetime
   * @param dateFormat
   */
  decodeConverterQueryParams(
    form: FormGroup,
    queryParams: any,
    datetimeControls: Array<string> = [],
    dateFormat: string = 'DD.MM.YYYYTHH:mm:ss',
  ) {
    for (const controlName in form.controls) {
      if (form.controls.hasOwnProperty(controlName)) {
        let value = queryParams[controlName];
        if (value) {
          if (datetimeControls.indexOf(controlName) >= 0) {
            value = moment(value, dateFormat).toDate();
          } else {
            if (value === 'false') {
              value = false;
            } else if (value === 'true') {
              value = true;
            } else if (value instanceof Array) {
              value = value.map((item) => {
                return urldecode(item);
              });
            } else {
              value = urldecode(value);
            }
          }
        }
        form.get(controlName)?.patchValue(value, { emitEvent: false });
      }
    }
    return form;
  }

  public setSortDirection(sortDirection: 'ASC' | 'DESC'): void {
    const currentState = this.storage.currentState();
    currentState.sortDirection = sortDirection;
    this.storage.changeState(currentState);
  }

  public setLimit(limit = '10'): void {
    const currentState = this.storage.currentState();
    currentState.limit = limit;
    this.storage.changeState(currentState);
  }

  public setSort(sort = '1'): void {
    const currentState = this.storage.currentState();
    currentState.sort = sort;
    this.storage.changeState(currentState);
  }
}
